
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CustomDataTable } from "@/components/table/CustomDataTable";
import { KolDataWithDates } from '@/types/table';
import { GroupedData } from './dataQualityUtils';

interface GroupedDataQualityTableProps {
  groupedData: GroupedData;
  columns: any[];
  loading: boolean;
  onRowEdit: (newRow: KolDataWithDates, oldRow: KolDataWithDates) => Promise<KolDataWithDates>;
}

export const GroupedDataQualityTable: React.FC<GroupedDataQualityTableProps> = ({
  groupedData,
  columns,
  loading,
  onRowEdit
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (picName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(picName)) {
      newExpanded.delete(picName);
    } else {
      newExpanded.add(picName);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(Object.keys(groupedData)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <Button onClick={expandAll} variant="outline" size="sm">
          Expand All
        </Button>
        <Button onClick={collapseAll} variant="outline" size="sm">
          Collapse All
        </Button>
      </div>

      {Object.entries(groupedData).map(([picName, records]) => (
        <div key={picName} className="border rounded-lg overflow-hidden">
          <div 
            className="flex items-center justify-between p-4 bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={() => toggleGroup(picName)}
          >
            <div className="flex items-center gap-3">
              {expandedGroups.has(picName) ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <Users className="h-4 w-4" />
              <span className="font-medium">{picName}</span>
              <Badge variant="secondary">
                {records.length} record{records.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </div>

          {expandedGroups.has(picName) && (
            <div className="border-t">
              <CustomDataTable
                data={records}
                columns={columns}
                loading={loading}
                onRowEdit={onRowEdit}
                enableColumnResizing={true}
                enableSorting={true}
                showPagination={false}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
